import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { hasPermission } from "../../../utils/helpers";
import { isActionRead } from "./esvActionDisplay";
import "../QC/QC.css";

export const ESV_CLEARANCE_FILTER_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "cleared", label: "Cleared" },
  { value: "", label: "All" },
];

export const ESV_ACTION_PERMISSIONS = {
  view: "view_automation_dashboard_esv_actions",
  readStatus: "edit_automation_dashboard_esv_actions_status",
  status: "edit_automation_dashboard_esv_actions_data_ops_status",
  assign: "edit_automation_dashboard_esv_actions_assign_to",
};

export const canViewEsvActions = () => hasPermission(ESV_ACTION_PERMISSIONS.view);

export const canEditEsvReadStatus = () => hasPermission(ESV_ACTION_PERMISSIONS.readStatus);

export const canEditEsvStatus = () => hasPermission(ESV_ACTION_PERMISSIONS.status);

export const canAssignEsvAction = () => hasPermission(ESV_ACTION_PERMISSIONS.assign);

export const normalizeEsvClearance = (value) => {
  const text = value == null ? "" : String(value).trim().toLowerCase();
  return text === "cleared" ? "cleared" : "pending";
};

const stopRowClick = (event) => {
  event.stopPropagation();
};

const nameFromAssigneeId = (assignedTo, assigneeOptions, userNames) => {
  if (assignedTo == null || assignedTo === "") return "";
  const options = Array.isArray(assigneeOptions) ? assigneeOptions : [];
  const match = options.find((option) => String(option.value) === String(assignedTo));
  if (match && match.label) return match.label;
  if (!userNames) return "";
  return userNames[assignedTo] || userNames[String(assignedTo)] || "";
};

const assigneeDisplayName = (assignedTo, assignedName, assigneeOptions, userNames) => {
  if (assignedName != null && String(assignedName).trim() !== "") {
    return String(assignedName).trim();
  }
  return nameFromAssigneeId(assignedTo, assigneeOptions, userNames);
};

export const mergeEsvAssignedToRow = (row, assignedTo, assignedToName, response = {}) => ({
  ...row,
  assigned_to: Object.prototype.hasOwnProperty.call(response, "assigned_to")
    ? response.assigned_to
    : assignedTo,
  assigned_to_name: Object.prototype.hasOwnProperty.call(response, "assigned_to_name")
    ? response.assigned_to_name
    : assignedToName,
});

export function EsvAssignedToCell({
  record,
  canEdit,
  assigneeOptions,
  assigneeLoading,
  userNames = {},
  onChange,
}) {
  const recordId = record?.id;
  const recordAssignedTo = record?.assigned_to;
  const recordAssignedName = record?.assigned_to_name;
  const [assignedTo, setAssignedTo] = useState(recordAssignedTo);
  const [saving, setSaving] = useState(false);
  const resolvedName = assigneeDisplayName(
    recordAssignedTo,
    recordAssignedName,
    assigneeOptions,
    userNames,
  );

  useEffect(() => {
    setAssignedTo(recordAssignedTo);
  }, [recordId, recordAssignedTo]);

  const assignOptions = useMemo(() => {
    const options = (Array.isArray(assigneeOptions) ? assigneeOptions : [])
      .filter((option) => option.value !== "");
    if (recordAssignedTo == null || recordAssignedTo === "" || !resolvedName) {
      return options;
    }
    return [
      { value: recordAssignedTo, label: resolvedName },
      ...options.filter((option) => String(option.value) !== String(recordAssignedTo)),
    ];
  }, [assigneeOptions, recordAssignedTo, resolvedName]);

  if (!canEdit) {
    return resolvedName || "-";
  }

  const assignToOnSelect = async (value) => {
    const previousValue = recordAssignedTo;
    setAssignedTo(value);
    setSaving(true);
    const selected = assignOptions.find((option) => String(option.value) === String(value));
    const response = await onChange(record, value, selected?.label ?? null);
    setSaving(false);
    if (!response?.success) {
      setAssignedTo(previousValue);
    }
  };

  return (
    <Select
      showSearch
      className="QCTable__AssignTo"
      popupClassName="QCTable__AssignTo__Menu"
      loading={assigneeLoading || saving}
      options={assignOptions}
      value={assignedTo}
      onSelect={assignToOnSelect}
      onClick={stopRowClick}
      onMouseDown={stopRowClick}
      filterOption={(inputValue, option) => (
        String(option?.label ?? "").toLowerCase().includes(inputValue.toLowerCase())
      )}
      listHeight={400}
    />
  );
}

export function EsvDataOpsStatusCell({ record }) {
  const clearance = normalizeEsvClearance(record?.dataops_status ?? record?.clearance);
  return <span>{clearance === "cleared" ? "Cleared" : "Pending"}</span>;
}

export function EsvReadStatusCell({ record }) {
  return <span>{isActionRead(record?.is_read) ? "Read" : "Unread"}</span>;
}
